import { z } from 'zod';

export const InteractionProtocolSchema = z.enum(['http', 'dns', 'smtp', 'ftp']);

export const InteractionSchema = z.object({
  id: z.string(),
  protocol: InteractionProtocolSchema,
  uniqueId: z.string(),
  fullId: z.string(),
  timestamp: z.string(),
  remoteAddress: z.string(),
  rawRequest: z.string().optional(),
  rawResponse: z.string().optional(),
  qType: z.string().optional(),
  httpMethod: z.string().optional(),
  httpPath: z.string().optional(),
  httpHeaders: z.record(z.string(), z.string()).optional(),
  httpBody: z.string().optional(),
});

// Session ID must be alphanumeric with optional hyphens/underscores
// This prevents path traversal attacks (e.g., ../../../etc/passwd)
export const SafeSessionIdSchema = z
  .string()
  .min(1)
  .max(64)
  .regex(
    /^[a-zA-Z0-9_-]+$/,
    'Session ID must only contain letters, numbers, hyphens, and underscores',
  );

export const GenerateLinkResponseSchema = z.object({
  domain: z.string(),
  sessionId: z.string(),
  expiresAt: z.string(),
});

export const ResumeSessionResponseSchema = z.object({
  domain: z.string(),
  sessionId: z.string(),
  expiresAt: z.string(),
});

export const GetInteractionsResponseSchema = z.object({
  sessionId: z.string(),
  count: z.number(),
  interactions: z.array(InteractionSchema),
});

export const StopListenerResponseSchema = z.object({
  success: z.boolean(),
});

export const DeleteSessionResponseSchema = z.object({
  success: z.boolean(),
});

export const SessionSourceSchema = z.enum(['ui', 'copilot', 'neo']);

export const SessionInfoSchema = z.object({
  sessionId: z.string(),
  domain: z.string(),
  expiresAt: z.string(),
  createdAt: z.string(),
  isActive: z.boolean(),
  interactionCount: z.number(),
  source: SessionSourceSchema,
});

export type SessionSource = z.infer<typeof SessionSourceSchema>;

export const ListSessionsResponseSchema = z.object({
  sessions: z.array(SessionInfoSchema),
});

export const DynamicResponseConfigSchema = z.object({
  body: z.string().optional(),
  status: z.number().min(100).max(599).optional(),
  headers: z.record(z.string(), z.string()).optional(),
  delay: z.number().min(0).max(30).optional(),
});

export const SetDynamicResponseResponseSchema = z.object({
  dynamicUrl: z.string(),
  sessionId: z.string(),
});

export const ErrorResponseSchema = z.object({
  message: z.string(),
});

export type InteractionProtocol = z.infer<typeof InteractionProtocolSchema>;
export type Interaction = z.infer<typeof InteractionSchema>;
export type GenerateLinkResponse = z.infer<typeof GenerateLinkResponseSchema>;
export type ResumeSessionResponse = z.infer<typeof ResumeSessionResponseSchema>;
export type GetInteractionsResponse = z.infer<
  typeof GetInteractionsResponseSchema
>;
export type StopListenerResponse = z.infer<typeof StopListenerResponseSchema>;
export type DeleteSessionResponse = z.infer<typeof DeleteSessionResponseSchema>;
export type SessionInfo = z.infer<typeof SessionInfoSchema>;
export type ListSessionsResponse = z.infer<typeof ListSessionsResponseSchema>;
export type DynamicResponseConfig = z.infer<typeof DynamicResponseConfigSchema>;
export type SetDynamicResponseResponse = z.infer<
  typeof SetDynamicResponseResponseSchema
>;
export type ErrorResponse = z.infer<typeof ErrorResponseSchema>;
